<template>
  <v-dialog
    content-class="link-domain-to-organization-popup"
    :value="internalValue"
    @keydown.esc="onCloseClick"
    @click:outside='onCloseClick'
    :width="width"
  >
    <v-card class="popup-wrapper" >
      <div class='close-action'>
        <span class='close_btn' @click='onCloseClick'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <div class="content">
        <div class="title">
          {{ title }}
        </div>
        <div class='link-domain-warning-note'>
          <v-alert
            outlined
            type="info"
          >
            {{ $t('link.domain.warning') }}
          </v-alert>
        </div>
        <div class='link-domains-form'>
          <v-text-field
            class="assets-search"
            v-model="search"
            :placeholder="$t('link.domain.search.placeholder')"
            prepend-inner-icon="search"
            dense
            outlined
            clearable
            hide-details
          />
          <div class='assets-list-toolbar' v-if='allItemsOptions.length'>
            <div class='assets-list-toolbar__select-all' @click='toggleSelectAll'>
              <v-icon class="material-icons-outlined select-icon" :color="anySelected ? '#8E30D0' : ''">
                {{ selectAllIcon }}
              </v-icon>
              <span class='label'>{{ $t('link.domain.select.all') }}</span>
            </div>
            <div class='assets-list-toolbar__count' v-if='linkedAssets.length'>
              {{ $t('link.domain.selected.count', { count: linkedAssets.length }) }}
            </div>
          </div>
          <div class='assets-list' v-if='filteredItems.length'>
            <div
              class='assets-list__row'
              :class="{ selected: isDomainSelected(item) }"
              v-for='item in filteredItems'
              :key='item.configData'
              @click='toggleSingle(item)'
            >
              <v-icon class="material-icons-outlined check" :color="isDomainSelected(item) ? '#8E30D0' : ''">
                {{ isDomainSelected(item) ? 'check_box' : 'check_box_outline_blank' }}
              </v-icon>
              <v-icon class="material-icons-outlined type" :color="isDomainSelected(item) ? '#8E30D0' : ''">
                {{ isItemCloudAccount(item) ? 'cloud' : 'language' }}
              </v-icon>
              <div class='assets-list__row__content'>
                <div class='name'>{{ item.configData }}</div>
                <div class='subtitle'>{{ item.organization.name }}</div>
              </div>
            </div>
          </div>
          <div class='assets-list-empty' v-else>
            {{ allItemsOptions.length ? $t('link.domain.no.results') : $t('link.domain.no.assets') }}
          </div>
        </div>
      </div>
      <div class="buttons">
        <v-btn small depressed class="cancel-button btn-secondary" @click="onCloseClick"> {{ $t('confirm.cancel') }} </v-btn>
        <v-btn small depressed class="save-button btn-primary" :disabled="saveButtonDisabled" @click="onSaveClick" :loading='loading'>
          {{ $t('save') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>

import { mapGetters } from 'vuex';
import { CONFIGURATION } from '@/constants/configuration';

export default {
  name: 'LinkDomainToOrganizationPopup',
  components: {
  },
  props: {
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    value: {
      required: true,
      type: Boolean,
    },
    width: {
      type: Number,
      required: false,
      default: 600,
    },
    organization: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      linkedAssets: [],
      search: '',
    };
  },
  watch: {
    internalValue() {
      this.linkedAssets = [];
      this.search = '';
    },
  },
  methods: {
    isItemCloudAccount(item) {
      return item.configType === CONFIGURATION.CONFIG_TYPES.CLOUD_ACCOUNT;
    },
    toggleSingle(item) {
      const index = this.linkedAssets.findIndex(asset => asset.configData === item.configData);
      if (index > -1) {
        this.linkedAssets.splice(index, 1);
      } else {
        this.linkedAssets.push(item);
      }
    },
    isDomainSelected(item) {
      return this.linkedAssets.some(asset => asset.configData === item.configData);
    },
    toggleSelectAll() {
      if (this.allFilteredSelected) {
        const filteredKeys = new Set(this.filteredItems.map(item => item.configData));
        this.linkedAssets = this.linkedAssets.filter(asset => !filteredKeys.has(asset.configData));
      } else {
        const selectedKeys = new Set(this.linkedAssets.map(asset => asset.configData));
        const toAdd = this.filteredItems.filter(item => !selectedKeys.has(item.configData));
        this.linkedAssets = [...this.linkedAssets, ...toAdd];
      }
    },
    async onSaveClick() {
      const payload = this.linkedAssets.map(asset => ({
        ...asset,
        data: asset.configData,
        organization: this.organization,
      }));
      await this.$emit('saveLinkedDomains', payload);
    },
    onCloseClick() {
      this.$emit('closed');
    },
  },
  computed: {
    ...mapGetters(['configurationDomains', 'domainsPerOrganization', 'configurationCloudAccounts', 'cloudAccountsPerOrganization']),
    currentDomainsLinked() {
      return this.domainsPerOrganization.get(this.organization.name) || [];
    },
    currentCloudAccountsLinked() {
      return this.cloudAccountsPerOrganization.get(this.organization.name) || [];
    },
    cloudAccountsOptions() {
      return [...this.configurationCloudAccounts]
        .filter(account => !this.currentCloudAccountsLinked.includes(account.configData));
    },
    domainOptions() {
      return [...this.configurationDomains]
        .filter(domain => !this.currentDomainsLinked.includes(domain.configData));
    },
    allItemsOptions() {
      return [...this.cloudAccountsOptions, ...this.domainOptions]
        .sort((a, b) => (a.configData > b.configData ? 1 : -1));
    },
    filteredItems() {
      const term = (this.search || '').trim().toLowerCase();
      if (!term) {
        return this.allItemsOptions;
      }
      return this.allItemsOptions.filter(item => item.configData.toLowerCase().includes(term));
    },
    allFilteredSelected() {
      return this.filteredItems.length > 0 &&
        this.filteredItems.every(item => this.isDomainSelected(item));
    },
    anySelected() {
      return this.filteredItems.some(item => this.isDomainSelected(item));
    },
    selectAllIcon() {
      if (this.allFilteredSelected) {
        return 'check_box';
      }
      return this.anySelected ? 'indeterminate_check_box' : 'check_box_outline_blank';
    },
    saveButtonDisabled() {
      return !this.linkedAssets.length;
    },
    title() {
      return this.$t('link.domain.popup.title');
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
};
</script>

<style lang='scss'>
.link-domain-to-organization-popup {
  .v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot {
    min-height: 40px;
  }
}
</style>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';
.link-domain-to-organization-popup {
  .popup-wrapper {
    max-width: rem(600);
    padding: $space-8 $space-10;
    .close-action {
      float: right;
      .close_btn {
        float: right;
        margin-top: rem(-15);
        margin-right: rem(-15);

        &:hover {
          cursor: pointer;
          color: var(--brand-primary);
        }
      }
    }
    .content {
      .title {
        margin-bottom: rem(15);
      }
      .link-domains-form {
        margin-top: $space-7;
        .assets-search {
          margin-bottom: $space-3;
        }
        .assets-list-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 rem(4) rem(6);
          border-bottom: 1px solid var(--border-primary);
          &__select-all {
            display: flex;
            align-items: center;
            cursor: pointer;
            .select-icon {
              margin-right: rem(8);
            }
            .label {
              font-size: $font-size-sm;
              color: var(--text-primary);
            }
          }
          &__count {
            font-size: $font-size-xs;
            color: var(--text-secondary);
          }
        }
        .assets-list {
          max-height: rem(280);
          overflow-y: auto;
          margin-top: rem(4);
          &__row {
            display: flex;
            align-items: center;
            padding: rem(8) rem(4);
            border-radius: $radius-xs;
            cursor: pointer;
            &:hover {
              background-color: var(--bg-tertiary);
            }
            &.selected {
              background-color: rgba(142, 48, 208, 0.08);
            }
            .check {
              margin-right: rem(10);
            }
            .type {
              margin-right: rem(10);
              color: var(--text-secondary);
            }
            &__content {
              display: flex;
              flex-direction: column;
              overflow: hidden;
              .name {
                font-size: $font-size-sm;
                color: var(--text-primary);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              .subtitle {
                font-size: $font-size-2xs;
                color: var(--text-secondary);
              }
            }
          }
        }
        .assets-list-empty {
          padding: rem(24) rem(4);
          text-align: center;
          font-size: $font-size-sm;
          color: var(--text-secondary);
        }
      }
    }

    .buttons {
      text-align: right;
      padding-top: rem(30);

      .cancel-button {
        height: $btn-height-md;
        border-radius: $radius-sm;
      }

      .save-button {
        height: $btn-height-md;
        border-radius: $radius-sm;
        margin-left: $space-2;
      }
    }
  }
}
</style>
